import _ from 'lodash';
import Debug from '@/util/debug';
import { swiper, swiperSlide } from 'vue-awesome-swiper';
import resize from 'vue-resize-directive';
import icons from '@/util/icons';
import Loading from '@/components/loading/Loading.vue';
import { STEP_FORMAT, STEP_SETTINGS } from '@/services/StepService';
import LeadFormPreview from '@/components/lead-form-preview/LeadFormPreview.vue';
import { GOAL_LEAD_GENERATION, GOAL_LOCAL_OFFERS } from '@/services/GoalTypeService';
import { MODE_SUBSCRIPTION } from '@/services/ModeService';
import CampaignFormatPopup from './CampaignFormatPopup.vue';

const debug = Debug('campaign-format');
const breakpointTablet = 991;
const breakpointMobile = 575;

export default {
  dependencies: [
    'formatService',
    'draftService',
    'goalTypeService',
    'genericConfigService',
    'localeService',
  ],
  directives: {
    resize,
  },
  components: {
    swiper,
    'swiper-slide': swiperSlide,
    loading: Loading,
    'lead-form-preview': LeadFormPreview,
    CampaignFormatPopup,
  },
  props: {
    campaign: Object,
  },
  data() {
    return {
      normalize: false,
      loading: true,
      error: null,
      allPostExamples: null,
      postExamples: null,
      originalType: null,
      swiperOption: {
        notNextTick: true,
        initialSlide: 0,
        slidesPerView: 3,
        centeredSlides: false,
        paginationClickable: false,
        nextButton: '.swiper-button-next',
        prevButton: '.swiper-button-prev',
        spaceBetween: 5,
        breakpoints: {
          [breakpointTablet]: {
            slidesPerView: 2,
            spaceBetween: 5,
            centeredSlides: true,
          },
          [breakpointMobile]: {
            slidesPerView: 1,
            spaceBetween: 5,
          },
        },
      },
      confirmOffsetLeft: 0,
      confirmWidth: 0, // 0 = 100%
      formatSwiperOptions: {
        nextButton: '.button.button-mobile',
        prevButton: '.button.button-desktop',
        autoplay: 5000,
      },
      icons,
      activeModalReference: false,
      leadConfig: {},
      creativeRefreshSettings: null,
      creativeRefreshCount: 0,
      language: null,
    };
  },
  methods: {
    save() {
      this.campaign.currentStep = STEP_FORMAT;
      if (this.creativeRefreshOptions) {
        this.campaign.details.creativeRefreshCount = this.creativeRefreshCount;
      } else {
        delete this.campaign.details.creativeRefreshCount;
      }
      this.draftService.updateDraft(this.$route.params.id, this.campaign)
        .then(id => {
          this.$router.push({
            name: STEP_SETTINGS,
            params: { id },
          });
        });
    },
    formatHeader(example) {
      let name = this.$t('pos.format.title', { format: example.type });
      if (example.extra_cost) {
        name += `<br />${this.$t('pos.format.title-extracost', { cost: example.extra_cost })}`;
      }
      return name;
    },
    selectExample(example) {
      if (example.extra_cost) {
        this.campaign.details.extraCostFormat = example.extra_cost;
      } else {
        delete this.campaign.details.extraCostFormat;
      }
      this.campaign.type = example.type;

      if (this.showNewCreative) {
        this.campaign.details.newcreative = 1;
      }

      this.calculateButtonOffset();
    },
    toggleFormatModal(example, show = true) {
      if (window.document.body.clientWidth > breakpointTablet) {
        const modalReference = `modal${example.type}`;

        if (this.$refs[modalReference]) {
          if (show) {
            this.activeModalReference = modalReference;
            this.$refs[modalReference][0].show();
            this.onTabDisplay();
          } else {
            this.$refs[modalReference][0].hide();
            this.activeModalReference = false;
          }
        }
      } else if (show) {
        this.selectExample(example);
      }
    },
    calculateButtonOffset() {
      const currentIdx = `slide${this.campaign.type}`;

      if (window.document.body.clientWidth <= breakpointTablet) {
        if (this.activeIndex === -1 && this.postExamples.length) {
          this.campaign.type = this.postExamples[0].type;
        }
        this.hideAllModals();
      }

      if (this.$refs.formatSwiper) {
        this.$refs.formatSwiper.swiper.slideTo(this.activeIndex); // Slide to correct slide
      }

      if (_.has(this, ['$refs', currentIdx, '0', '$el'])) {
        this.confirmOffsetLeft = this.$refs[currentIdx][0].$el.offsetLeft;
        this.confirmWidth = this.$refs[currentIdx][0].$el.clientWidth;
      }
    },
    triggerUpdate(swiperReference) {
      // Swiper isn't loaded with page, this fixes swiper slide position
      if (this.$refs[swiperReference]) {
        this.$nextTick(() => {
          this.$refs[swiperReference][0].swiper.init();
        });
      }
    },
    hideAllModals() {
      this.postExamples.forEach(ex => {
        const modal = `modal${ex.type}`;
        if (this.$refs[modal]) {
          this.$refs[modal][0].hide();
        }
      });
    },
    onTabDisplay(tabIndex) {
      if (this.activeModalReference) {
        let currentTab = tabIndex;

        if (!Number.isInteger(currentTab)) {
          currentTab = this.$refs[`${this.activeModalReference}tabs`][0].$data.currentTab;
        }

        const leadAdReference = `${this.activeModalReference}leadAd`;

        switch (currentTab) {
          case 0:
            this.triggerUpdate(`${this.activeModalReference}swiper`);
            break;
          case 1:
            if (this.$refs[leadAdReference]) {
              this.$refs[leadAdReference][0].triggerUpdate();
            }
            break;
          default:
        }
      }
    },
    cancel(ex) {
      this.toggleFormatModal(ex, false);
    },
    confirm(ex) {
      this.selectExample(ex);
      this.toggleFormatModal(ex, false);
    },
    getActiveConfirmButton(type) {
      const modal = `modal${type}`;
      return this.activeModalReference === modal ? type : null;
    },
    localizePostExamples() {
      if (this.allPostExamples !== null) {
        const filteredEls = this.allPostExamples.filter(e => e.language === this.language);
        if (Object.keys(filteredEls).length > 0) {
          this.postExamples = filteredEls;
        } else {
          this.postExamples = this.allPostExamples.filter(e => e.language === null);
        }
      }
    },
  },
  computed: {
    proceed: function computedProceed() {
      if (this.activeIndex === -1) {
        if (this.campaign.type !== null) {
          this.$toast.warn({ message: this.$t('pos.format.reset') });
        }
        this.campaign.type = null;
        return false;
      }
      return true;
    },
    activeIndex: function computedActiveIndex() {
      return this.postExamples.findIndex(elem => elem.type === this.campaign.type);
    },
    isActiveLeadPreview() {
      return this.campaign.goal_type === GOAL_LEAD_GENERATION;
    },
    isLocalOffersPreview() {
      return this.campaign.goal_type === GOAL_LOCAL_OFFERS;
    },
    creativeRefreshOptions() {
      if (_.get(this.creativeRefreshSettings, 'is_on_demand_refresh') && this.campaign.mode === MODE_SUBSCRIPTION) {
        const until = _.get(this.creativeRefreshSettings, 'on_demand_refresh_count', 0);
        return [...Array(until + 1).keys()].map(value => ({ value, text: value }));
      }
      return false;
    },
    showNewCreative() {
      return _.has(this.campaign, 'details.newcreative');
    },
    disabledNewCreative() {
      return this.showNewCreative && this.campaign.type !== this.originalType;
    },
  },
  created() {
    this.creativeRefreshCount = _.get(this.campaign, 'details.creativeRefreshCount', 0);
  },
  mounted() {
    const fin = () => {
      debug.info('finished loading. error: %o', this.error);
      this.loading = false;
    };

    this.originalType = this.campaign.type;

    Promise.all([
      this.goalTypeService.getOptions(),
      this.formatService.getPostExamples(),
      this.localeService.getUserLanguage(),
      this.goalTypeService.getLeadConfig().then(res => {
        this.leadConfig = res;
      }),
      this.genericConfigService.getConfig().then(c => {
        this.creativeRefreshSettings = c.creative_refresh_settings;
      }),
    ])
    .then(([goalTypes, postExamples, userLanguage]) => {
      this.language = userLanguage;
      this.normalize = postExamples.length > 3;

      const goalType = _.get(goalTypes, this.campaign.goal_type, null);
      this.allPostExamples = postExamples.filter(fmt =>
        !goalType || _.has(goalType.ad_formats, fmt.type));
      this.localizePostExamples();

      this.swiperOption.slidesPerView = this.postExamples.length;
      this.swiperOption.initialSlide = this.activeIndex;
    })
    .then(fin)
    .catch(e => { this.error = e; fin(); });

    this.localeService.getEventBus().$on('language-changed', language => {
      this.language = language;
      this.localizePostExamples();
    });

    setTimeout(() => {
      this.calculateButtonOffset();
      if (this.$refs.formatSwiper) {
        this.$refs.formatSwiper.swiper.on('onSlideChangeStart', (sw) => {
          this.selectExample(this.postExamples[sw.activeIndex]); // Change format on slide
        });
      }
    }, 700);
  },
  beforeDestroy() {
    this.localeService.getEventBus().$off('language-changed', language => this.language = language);
  },
};



// WEBPACK FOOTER //
// ./src/components/campaign-format/CampaignFormat.js